<template>
  <ValidationObserver
    ref="form"
    v-slot="{ invalid, handleSubmit, pristine }"
    slim
  >
    <form
      role="form"
      class="user form-horizontal"
      @submit.prevent="onSubmit"
    >
      <fieldset>
        <Field
          :label="$t('INPUT_LABEL_FIRST_NAME')"
          :has-tooltip="true"
          :tooltip-text="$t('TT_FIRST_NAME')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="firstName"
              rules="required"
              name="firstName"
              slim
            >
              <div :class="classes">
                <div class="col-sm-4 col-md-2">
                  <input
                    v-model="firstName"
                    type="text"
                    name="firstName"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('INPUT_LABEL_LAST_NAME')"
          :has-tooltip="true"
          :tooltip-text="$t('TT_LAST_NAME')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="lastName"
              rules="required"
              name="lastName"
              slim
            >
              <div :class="classes">
                <div class="col-sm-5 col-md-2">
                  <input
                    v-model="lastName"
                    type="text"
                    name="lastName"
                    autocomplete="off"
                    class="form-control"
                  >
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('INPUT_LABEL_EMAIL')"
          :has-tooltip="true"
          :tooltip-text="$t('TT_EMAIL')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes, dirty, valid }"
              ref="email"
              rules="required|email"
              name="email"
              slim
            >
              <div :class="classes">
                <div class="col-sm-6 col-md-4">
                  <input
                    v-model="email"
                    type="email"
                    name="email"
                    autocomplete="off"
                    class="form-control"
                  >
                  <span
                    v-if="dirty && !valid"
                    v-t="'INVALID_EMAIL_LABEL'"
                    class="error-details"
                  />
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('INPUT_LABEL_CONFIRM_EMAIL')"
          :has-tooltip="true"
          :tooltip-text="$t('TT_CONFIRM_EMAIL')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes, dirty, valid }"
              ref="confirmEmail"
              name="confirmEmail"
              rules="required|email|confirmed:email"
              slim
            >
              <div :class="classes">
                <div class="col-sm-6 col-md-4">
                  <input
                    v-model="confirmEmail"
                    type="email"
                    name="confirmEmail"
                    autocomplete="off"
                    class="form-control"
                  >
                  <span
                    v-if="dirty && !valid"
                    v-t="'INVALID_CONFIRM_EMAIL_LABEL'"
                    class="error-details"
                  />
                </div>                   
                <label 
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('INPUT_LABEL_ROLE')"
          :has-tooltip="true"
          :tooltip-text="$t('TT_ROLE')"
        >
          <template #element>
            <ValidationProvider
              slim
            >
              <div class="col-sm-3 col-md-3">
                <MultipleToggle
                  ref="role"
                  name="role"
                  :value="role"
                  :options="rolesOptions"
                  @onToggle="onRoleChange"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <Field
          v-if="role == 'user'"
          :label="$t('INPUT_LABEL_CAN_EXPORT')"
          :has-tooltip="true"
          :tooltip-text="$t('TT_CAN_EXPORT')"
        >
          <template #element>
            <ValidationProvider
              slim
            >
              <div class="col-sm-3 col-md-3">
                <nzToggle
                  v-model="canExport"
                  @onToggle="onCanExportChange"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <div class="form-group">
          <div class="col-sm-8 col-md-8 col-md-offset-3 col-sm-offset-3">       
            <p
              v-if="triedToSend && invalid"
              class="m-t-8 m-b-8 bright-red"
            >
              <i class="f-s-20 p-r-0 typcn typcn-warning" />{{ $t('INVALID_FORM') }}
            </p>     
            <PrimaryButton
              size="sm"
              :disabled="pristine"
              :show-loader-on-form="showLoader"
            >
              {{ $t('ACTION_CREATE_INVITE') }}
            </PrimaryButton>

            <button
              v-t="'CANCEL'"
              class="btn btn-sm btn-danger m-l-15"
              @click="cancel"
            />
          </div>
        </div>
      </fieldset>
    </form>
  </ValidationObserver>
</template>>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';
import nzToggle from '@/core/components/nzToggle/nzToggle';

export default {
  name: 'InviteUsersForm',
  components: {nzToggle},
  props: {
    showLoader: {
      type: Boolean,
      default: () => true
    }
  },
  data() {
    return {
      firstName: this.firstName ?? '',
      lastName: this.lastName ?? '',
      email: this.email ?? '',
      confirmEmail: this.confirmEmail ?? '',
      role: this.role ?? 'admin',
      canExport: false,
      rolesOptions: Object.freeze([
        { name: this.$t('ACCOUNT_USER_ADMIN_ROLE'), value: 'admin' },
        { name: this.$t('ACCOUNT_USER_REPORT_ROLE'), value: 'user' }
      ]),
      triedToSend: false
    };
  },
  methods:{
    onSubmit() {
      if(!this.$refs.form.flags.valid) {
        this.triedToSend = true;
        this.$refs.firstName.setFlags({
          touched: true
        });
        this.$refs.lastName.setFlags({
          touched: true
        });
        this.$refs.email.setFlags({
          touched: true
        });
        this.$refs.confirmEmail.setFlags({
          touched: true
        });
        this.$refs.form.validate();
      } else {

        const inviteFormValues = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          confirmEmail: this.confirmEmail,
          role: this.role,
        };

        if(this.role == 'user' && this.canExport){
          inviteFormValues.optionalPermissions = ['report_export'];
        }


        this.$emit('submit', inviteFormValues);
      }
    },
    cancel() {
      this.$emit('cancel');
    },
    onRoleChange(option) {
      this.role = option.value;
    },
    onCanExportChange(canExport) {
      this.canExport = canExport;
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>